import { useCallback } from 'react';
import { isNil } from 'lodash';
import { useErrorBoundary } from 'react-error-boundary';

import { useDialog } from '#packages/web/hooks/use-dialog';
import { Clock } from '#packages/data-collection/services/clock';

import { usePagination } from '#apps/client-web/app/hooks/use-pagination';
import { useLogger } from '#apps/client-web/app/hooks/use-logger';

import { useAuditListQuery } from '../../hooks';
import type { NewAudit } from '../../models/audit';

export type AuditListPageViewModel = ReturnType<
  typeof useAuditListPageViewModel
>;

export const useAuditListPageViewModel = () => {
  const logger = useLogger('features/audit/pages/audit-list/view-model');

  const { showBoundary } = useErrorBoundary();

  const createAuditDialog = useDialog<NewAudit>();

  const { pageNumber, pageSize } = usePagination({
    pageNumber: 1,
    pageSize: 50,
  });

  const auditListQuery = useAuditListQuery({
    pagination: { pageNumber: pageNumber, pageSize: pageSize },
  });

  const createAudit = useCallback(() => {
    logger.debug('Opening create audit dialog');

    showBoundary(new Error('Test error boundary from audit list view model'));

    createAuditDialog.open({
      title: `New Audit - ${Intl.DateTimeFormat(navigator.language).format(Clock.now.toDate())}`,
      types: ['ea'],
    });
  }, [createAuditDialog.open, logger]);

  const createAuditCallback = useCallback(
    (data: NewAudit) => {
      logger.debug({ data }, 'Creating new audit from dialog data');

      createAuditDialog.close();
    },
    [logger, createAuditDialog.close],
  );

  const [audits, pagination] = auditListQuery.data ?? [];

  const hasAudits = !isNil(audits) && audits.length > 0;

  return {
    /* List */
    isLoading: auditListQuery.isLoading,
    isError: auditListQuery.isError,
    error: auditListQuery.error,

    audits,
    hasAudits,
    pagination,

    /* Create */
    createAuditDialog,
    createAudit,
    createAuditCallback,
    isCreating: false,
  };
};
